import { useState, useEffect, useCallback } from 'react'
import ictaApi from '../api/ictaApi'
import {
	APIError,
	Batch,
	BatchFilters,
	CreateBatchPayload,
	CreateBatchResponse,
	GetBatchesResponse,
	GetBatchResponse,
	UpdateBatchPayload,
	UpdateBatchResponse,
	AddTestPayload,
	AddTestResponse,
	AddHistoryPayload,
	AddHistoryResponse,
	DeleteBatchResponse,
	GetFileResponse,
	StockReportFilters,
	StockReportResponse,
	MovementHistoryFilters,
	MovementHistoryResponse,
	TestHistoryFilters,
	TestHistoryResponse
} from '../models/batch'

export const useBatch = () => {
	const [batches, setBatches] = useState<Batch[]>([])
	const [selectedBatch, setSelectedBatch] = useState<Batch | null>(null)
	const [loadingList, setLoadingList] = useState(false)
	const [loadingItem, setLoadingItem] = useState(false)
	const [creating, setCreating] = useState(false)
	const [updating, setUpdating] = useState(false)
	const [addingTest, setAddingTest] = useState(false)
	const [addingHistory, setAddingHistory] = useState(false)
	const [deleting, setDeleting] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [total, setTotal] = useState<number>(0)
	const [loadingReport, setLoadingReport] = useState(false)
	const [stockReport, setStockReport] = useState<StockReportResponse | null>(null)

	const parseErrors = (errors?: APIError[]) => {
		if (!errors || errors.length === 0) return null
		return errors[0].msg || 'Error desconocido'
	}

	const fetchBatches = useCallback(async (begin = 0, end = 25, filters?: Omit<BatchFilters, 'begin' | 'end'>) => {
		setLoadingList(true)
		setError(null)
		try {
			const params = { begin, end, ...filters };
			const { data } = await ictaApi.get<GetBatchesResponse>('/batch', { params })
			const msg = parseErrors(data.errors)
			if (msg) setError(msg)
			else {
				setBatches(data.batches)
				setTotal(data.total)
			}
		} catch (err: any) {
			setError(parseErrors(err.response.data.errors) || 'Error al obtener lotes')
		} finally {
			setLoadingList(false)
		}
	}, [])

	const fetchBatch = useCallback(async (uid: string) => {
		setLoadingItem(true)
		setError(null)
		try {
			const { data } = await ictaApi.get<GetBatchResponse>(`/batch/${uid}`)
			const msg = parseErrors(data.errors)
			if (msg) setError(msg)
			else if (data.batch) setSelectedBatch(data.batch)
		} catch (err: any) {
			setError(parseErrors(err.response.data.errors) || 'Error al obtener lote')
		} finally {
			setLoadingItem(false)
		}
	}, [])

	const createBatch = useCallback(async (payload: CreateBatchPayload) => {
		setCreating(true)
		setError(null)
		try {
			const { data } = await ictaApi.post<CreateBatchResponse>('/batch', payload)
			const msg = parseErrors(data.errors)
			if (msg) {
				setError(msg)
				return null
			}
			if (data.batch) {
				setBatches((prev) => [data.batch!, ...prev])
				return data.batch
			}
			return null
		} catch (err: any) {
			setError(parseErrors(err.response.data.errors) || 'Error al crear lote')
			return null
		} finally {
			setCreating(false)
		}
	}, [])

	const updateBatch = useCallback(
		async (uid: string, payload: UpdateBatchPayload) => {
			setUpdating(true)
			setError(null)
			try {
				const { data } = await ictaApi.put<UpdateBatchResponse>(`/batch/${uid}`, payload)
				const msg = parseErrors(data.errors)
				if (msg) {
					setError(msg)
					return null
				}
				if (data.batch) {
					setBatches((prev) => prev.map((b) => (b.uid === uid ? data.batch! : b)))
					if (selectedBatch?.uid === uid) setSelectedBatch(data.batch)
					return data.batch
				}
				return null
			} catch (err: any) {
				setError(parseErrors(err.response.data.errors) || 'Error al actualizar lote')
				return null
			} finally {
				setUpdating(false)
			}
		},
		[selectedBatch]
	)

	const addTest = useCallback(
		async (uid: string, payload: AddTestPayload) => {
			setAddingTest(true)
			setError(null)
			try {
				const { data } = await ictaApi.post<AddTestResponse>(`/batch/add-test/${uid}`, payload)
				const msg = parseErrors(data.errors)
				if (msg) {
					setError(msg)
					return null
				}
				if (data.batch) {
					setBatches((prev) => prev.map((b) => (b.uid === uid ? data.batch! : b)))
					if (selectedBatch?.uid === uid) setSelectedBatch(data.batch)
					return data.batch
				}
				return null
			} catch (err: any) {
				setError(parseErrors(err.response.data.errors) || 'Error al agregar prueba')
				return null
			} finally {
				setAddingTest(false)
			}
		},
		[selectedBatch]
	)

	const addHistory = useCallback(
		async (uid: string, payload: AddHistoryPayload) => {
			setAddingHistory(true)
			setError(null)
			try {
				const { data } = await ictaApi.post<AddHistoryResponse>(
					`/batch/add-history/${uid}`,
					payload
				)
				const msg = parseErrors(data.errors)
				if (msg) {
					setError(msg)
					return null
				}
				if (data.batch) {
					setBatches((prev) => prev.map((b) => (b.uid === uid ? data.batch! : b)))
					if (selectedBatch?.uid === uid) setSelectedBatch(data.batch)
					return data.batch
				}
				return null
			} catch (err: any) {
				setError(parseErrors(err.response.data.errors) || 'Error al agregar historial')
				return null
			} finally {
				setAddingHistory(false)
			}
		},
		[selectedBatch]
	)

	const deleteBatch = useCallback(async (uid: string) => {
		setDeleting(true)
		setError(null)
		try {
			const { data } = await ictaApi.delete<DeleteBatchResponse>(`/batch/${uid}`)
			const msg = parseErrors(data.errors)
			if (msg) {
				setError(msg)
				return false
			}
			setBatches((prev) => prev.filter((b) => b.uid !== uid))
			return true
		} catch (err: any) {
			setError(parseErrors(err.response.data.errors) || 'Error al eliminar lote')
			return false
		} finally {
			setDeleting(false)
		}
	}, [])

	const getFile = useCallback(async (filePath: string) => {
		setLoadingItem(true)
		setError(null)
		try {
			const { data } = await ictaApi.post<GetFileResponse>(`/batch/get-file`, { path: filePath })
			const msg = parseErrors(data.errors)
			if (msg) {
				setError(msg)
				return null
			}
			return data.data || null
		} catch (err: any) {
			setError(parseErrors(err.response.data.errors) || 'Error al obtener archivo')
			return null
		} finally {
			setLoadingItem(false)
		}
	}, [])

	const generateStockReport = useCallback(async (filters: StockReportFilters) => {
		setLoadingReport(true)
		setError(null)
		try {
			const { data } = await ictaApi.post<StockReportResponse>('/batch/stock', filters)
			const msg = parseErrors(data.errors)
			if (msg) {
				setError(msg)
				return null
			}
			setStockReport(data)
			return data
		} catch (err: any) {
			setError(parseErrors(err.response.data.errors) || 'Error al generar reporte')
			return null
		} finally {
			setLoadingReport(false)
		}
	}, [])

	const generateMovementHistory = useCallback(async (filters: MovementHistoryFilters) => {
		setLoadingReport(true)
		setError(null)
		try {
			const { data } = await ictaApi.post<MovementHistoryResponse>('/batch/movement-history', filters)
			const msg = parseErrors(data.errors)
			if (msg) {
				setError(msg)
				return null
			}
			return data
		} catch (err: any) {
			setError(parseErrors(err.response.data.errors) || 'Error al generar historial de movimientos')
			return null
		} finally {
			setLoadingReport(false)
		}
	}, [])

	const generateTestHistory = useCallback(async (filters: TestHistoryFilters) => {
		setLoadingReport(true)
		setError(null)
		try {
			const { data } = await ictaApi.post<TestHistoryResponse>('/batch/test-history', filters)
			const msg = parseErrors(data.errors)
			if (msg) {
				setError(msg)
				return null
			}
			return data
		} catch (err: any) {
			setError(parseErrors(err.response.data.errors) || 'Error al generar historial de pruebas')
			return null
		} finally {
			setLoadingReport(false)
		}
	}, [])

	// useEffect(() => {
	// 	fetchBatches()
	// }, [fetchBatches])

	// Alert de error
	useEffect(() => {
		if (error) {
			alert(`Error: ${error}`)
			setError(null) // Reset error after showing alert
		}
	}, [error])

	return {
		batches,
		selectedBatch,
		loadingList,
		loadingItem,
		creating,
		updating,
		addingTest,
		addingHistory,
		deleting,
		error,
		total,
		loadingReport,
		stockReport,
		fetchBatches,
		fetchBatch,
		createBatch,
		updateBatch,
		addTest,
		addHistory,
		deleteBatch,
		getFile,
		generateStockReport,
		generateMovementHistory,
		generateTestHistory
	}
}
